import React, { useMemo, useState } from "react";
import { Table, Badge } from "../ui";
import { FaEye, FaPen, FaTrash, ChevronLeft, ChevronRight, ChevronDoubleLeft, ChevronDoubleRight } from "../ui/Icons";
import ActionButtons from "./ActionButtons";
import { API_BASE_URL } from "../../config";

/* ── Pagination helpers ──────────────────────────────── */
const buildPageRange = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, "…", total);
  } else if (current >= total - 3) {
    pages.push(1, "…", total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, "…", current - 1, current, current + 1, "…", total);
  }
  return pages;
};

/* ── Status Badge ────────────────────────────────────── */
const StatusBadge = ({ value }) => {
  const normalized = String(value).toLowerCase();
  const variant =
    normalized === "verified" || normalized === "active" ? "success"
      : normalized === "pending" ? "warning"
        : normalized === "rejected" || normalized === "closed" ? "danger"
          : "secondary";
  const displayText = normalized === "verified" ? "Active" : normalized === "pending" ? "Pending" : value;
  return (
    <Badge variant={variant}>
      {displayText}
    </Badge>
  );
};


/* ── Page Button ─────────────────────────────────────── */
const PageButton = ({ children, isActive, disabled, onClick, iconOnly = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "inline-flex items-center justify-center rounded-lg border text-xs font-medium transition-colors duration-150",
      "focus:outline-none focus:ring-2 focus:ring-primary/30",
      "disabled:opacity-40 disabled:cursor-not-allowed",
      iconOnly ? "w-7 h-7" : "min-w-[28px] h-7 px-2",
      isActive
        ? "bg-primary text-white border-primary"
        : "bg-white text-ink-700 border-border hover:bg-canvas hover:text-ink-900",
    ].filter(Boolean).join(" ")}
  >
    {children}
  </button>
);

/* ── Action Icon Button ──────────────────────────────── */
const ActionIconBtn = ({ onClick, title, children, color = "default" }) => {
  const colorClass = {
    default: "text-info-600 bg-info-50 border-info-200 hover:bg-info-100 hover:text-info-700",
    edit: "text-warning-600 bg-warning-50 border-warning-200 hover:bg-warning-100 hover:text-warning-700",
    danger: "text-danger-600 bg-danger-50 border-danger-200 hover:bg-danger-100 hover:text-danger-700",
  }[color] ?? "text-info-600 bg-info-50 border-info-200 hover:bg-info-100 hover:text-info-700";

  return (
    <button
      onClick={onClick}
      title={title}
      className={[
        "inline-flex items-center justify-center w-7 h-7 rounded-md border shadow-sm",
        "transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20",
        colorClass,
      ].join(" ")}
    >
      {children}
    </button>
  );
};

/* ── ServerTablePagination ───────────────────────────── */
const ServerTablePagination = ({
  tabledata,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  imagecolumns,
  showViewOption,
  showEditOption,
  showDeleteOption,
  onClickView,
  onClickEdit,
  onClickDel,
  actionButtons,
  visibleFields,
  updateList,
  roleName,
  entity,
  customHeaders,
  customAction,
  cellClassNames,
  headerClassNames,
}) => {
  const [selectedColumn, setSelectedColumn] = useState(null);
  const totalPages = Math.ceil((totalCount || 0) / rowsPerPage);
  const imageColumnsExist = Array.isArray(imagecolumns) && imagecolumns.length > 0;

  /* ── Columns ─────────────────────────────────────── */
  const columns = useMemo(() => {
    if (visibleFields) return visibleFields;
    if (customHeaders) return Object.keys(customHeaders);
    if (!tabledata || tabledata.length === 0) return [];
    switch (entity) {
      case "accounts": return ["customerUniqueId", "transactionId", "transactionType", "accountType", "cash_type", "amount", "date"];
      case "roles": return ["roleName"];
      // Same list as TablePagination.js:67 — change both. staffId is an internal
      // sequence number, hidden from the table but still fetched and stored.
      case "staff": return ["name", "designation", "branchName"];
      default: return Object.keys(tabledata[0]).filter((c) => c !== "id");
    }
  }, [tabledata, entity, visibleFields, customHeaders]);

  /* ── Header label ────────────────────────────────── */
  const getHeaderLabel = (col) => {
    if (customHeaders?.[col]) return customHeaders[col].label || customHeaders[col];
    const map = {
      customerUniqueId: "Customer ID", transactionId: "Transaction ID",
      transactionType: "Transaction Type", accountType: "Account Type",
      cash_type: "Cash Type", amount: "Amount", date: "Date",
      roleName: "Role Name", staffId: "Staff ID", name: "Name",
      // Without this the fallback below splits on the capital: "branch Name".
      branchName: "Branch",
    };
    return map[col] ?? col.replace(/([A-Z])/g, " $1").trim();
  };

  /* ── Cell renderer ───────────────────────────────── */
  const renderCell = (column, row) => {
    const value = row[column];

    if (imageColumnsExist && imagecolumns.includes(column) && value) {
      return (
        <img
          src={`${API_BASE_URL}/images/${value}`}
          alt=""
          className="w-8 h-8 rounded object-cover mx-auto"
        />
      );
    }
    if (column === "date" && value) {
      return value.split("-").reverse().join("-");
    }
    if (column === "amount" && value != null) {
      return <span className="font-data tabular-nums">{Number(value).toLocaleString("en-IN")}</span>;
    }
    if (column === "status" && value) {
      return <StatusBadge value={value} />;
    }
    return value ?? <span className="text-[#CBD5E1]">—</span>;
  };

  /* ── Page range ──────────────────────────────────── */
  const pageRange = buildPageRange(page, totalPages);
  const startRow = (page - 1) * rowsPerPage + 1;
  const endRow = Math.min(page * rowsPerPage, totalCount || 0);
  const hasActions = showViewOption || showEditOption || showDeleteOption || customAction;

  return (
    <div className="flex-1 min-h-0 flex flex-col justify-between gap-4">
      {/* ── Table ───────────────────────────────────── */}
      <Table className="flex-1 min-h-0 overflow-y-auto">
        <Table.Head>
          <tr>
            {/* # column */}
            <Table.HeadCell align="center" className="w-[5%] min-w-[50px] whitespace-nowrap">#</Table.HeadCell>

            {columns.map((col, idx) => {
              const align = ["status", "actions"].includes(col) ? "center" : "left";
              let widthClass = "w-[15%] min-w-[110px]"; // default
              if (col === "name") widthClass = "w-[15%] min-w-[120px]";
              else if (col === "customerUniqueId") widthClass = "w-[18%] min-w-[120px]";
              else if (col === "fieldOfficerName") widthClass = "w-[20%] min-w-[130px]";
              else if (col === "status") widthClass = "w-[10%] min-w-[90px]";

              return (
                <Table.HeadCell
                  key={col}
                  align={align}
                  className={[
                    widthClass,
                    "cursor-pointer select-none transition-colors duration-150",
                    selectedColumn === idx
                      ? "bg-primary text-white"
                      : "hover:bg-canvas",
                    headerClassNames?.[col] ?? "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => setSelectedColumn(selectedColumn === idx ? null : idx)}
                >
                  {getHeaderLabel(col)}
                </Table.HeadCell>
              );
            })}

            {hasActions && (
              <Table.HeadCell align="center" className="w-[12%] min-w-[150px] whitespace-nowrap">Actions</Table.HeadCell>
            )}
          </tr>
        </Table.Head>

        <Table.Body>
          {tabledata && tabledata.length > 0 ? (
            tabledata.map((row, rowIdx) => (
              <Table.Row key={row.id ?? rowIdx}>
                {/* Row number */}
                <Table.Cell align="center" className="w-[5%] min-w-[50px] whitespace-nowrap text-ink-500 text-xs font-medium tabular-nums">
                  {(page - 1) * rowsPerPage + rowIdx + 1}
                </Table.Cell>

                {columns.map((col, colIdx) => {
                  const cellAttrs = row._html_attributes?.[col] ?? {};
                  const align = ["status", "actions"].includes(col) ? "center" : "left";
                  return (
                    <Table.Cell
                      key={col}
                      align={align}
                      className={[
                        selectedColumn === colIdx ? "bg-primary/5" : "",
                        cellClassNames?.[col] ?? "",
                      ].filter(Boolean).join(" ")}
                      {...cellAttrs}
                    >
                      {renderCell(col, row)}
                    </Table.Cell>
                  );
                })}

                {hasActions && (
                  <Table.ActionCell align="center" className="w-[12%] min-w-[150px] whitespace-nowrap">
                    <div className="flex items-center gap-2 justify-center">
                      {customAction && customAction(row.id)}
                      {showViewOption && (
                        <ActionIconBtn
                          onClick={() => onClickView(row.id)}
                          title="View"
                          color="default"
                        >
                          <FaEye size={13} />
                        </ActionIconBtn>
                      )}
                      {/* Accepts a boolean OR a per-row predicate, so a table can
                          hide Edit on individual rows (a disbursed loan) without
                          losing it for the rest. */}
                      {(typeof showEditOption === "function"
                        ? showEditOption(row)
                        : showEditOption) && (
                        <ActionIconBtn
                          onClick={() => onClickEdit(row.id)}
                          title="Edit"
                          color="edit"
                        >
                          <FaPen size={12} />
                        </ActionIconBtn>
                      )}
                      {showDeleteOption && (
                        <ActionIconBtn
                          onClick={() => onClickDel(row.id)}
                          title="Delete"
                          color="danger"
                        >
                          <FaTrash size={12} />
                        </ActionIconBtn>
                      )}
                      {actionButtons && (
                        <ActionButtons item={row} updateList={updateList} />
                      )}
                    </div>
                  </Table.ActionCell>
                )}
              </Table.Row>
            ))
          ) : (
            <Table.EmptyRow
              colSpan={columns.length + (hasActions ? 2 : 1)}
              message="No records found"
            />
          )}
        </Table.Body>
      </Table>

      {/* ── Pagination bar ──────────────────────────── */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-end gap-3 px-1 mt-1">
          {/* Left: info + rows-per-page (Commented out per user request) */}
          {/* 
          <div className="flex items-center gap-3 text-xs text-ink-500 font-semibold">
            <span>
              Showing <span className="text-ink-900">{startRow}–{endRow}</span>{" "}
              of <span className="text-ink-900">{totalCount}</span> entries
            </span>
            <div className="flex items-center gap-1.5">
              <span className="whitespace-nowrap font-normal">Rows:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
                className={[
                  "h-7 rounded-lg border border-border bg-white text-ink-900 text-xs font-semibold",
                  "px-2 pr-6 appearance-none cursor-pointer",
                  "focus:outline-none focus:ring-2 focus:ring-primary/30",
                  "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%2394A3B8' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")] bg-no-repeat bg-right-1 bg-[length:16px]",
                ].join(" ")}
              >
                {[5, 10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
          */}

          {/* Right: page buttons */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <PageButton iconOnly disabled={page <= 1} onClick={() => onPageChange(1)}>
                <ChevronDoubleLeft />
              </PageButton>
              <PageButton iconOnly disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                <ChevronLeft />
              </PageButton>

              {pageRange.map((p, i) =>
                p === "…" ? (
                  <span key={`ellipsis-${i}`} className="inline-flex items-center justify-center w-7 h-7 text-xs text-ink-300">…</span>
                ) : (
                  <PageButton
                    key={p}
                    isActive={p === page}
                    onClick={() => onPageChange(p)}
                  >
                    {p}
                  </PageButton>
                )
              )}

              <PageButton iconOnly disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
                <ChevronRight />
              </PageButton>
              <PageButton iconOnly disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>
                <ChevronDoubleRight />
              </PageButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServerTablePagination;
